import type { CreatorMinimalApi } from '@ingenico/api-sdk-device-management-private-v2';

export type GenericUuid = string;

export type ReadOnlyDate = Date;

export type GenericUuidReadOnly = string;

export type EnglishFamilyAndGivenNameReadOnly = string;

export const enumConfigurationResourceSoftwareItem = {
  CONFIGURATION_MODULE: 'CONFIGURATION_MODULE',
  SOFTWARE_VERSION_NO_PARAM: 'SOFTWARE_VERSION_NO_PARAM',
} as const;
export type EnumConfigurationResourceSoftwareItem =
  (typeof enumConfigurationResourceSoftwareItem)[keyof typeof enumConfigurationResourceSoftwareItem];

export interface ConfigurationResourceSoftwareVersionMinimalV2 {
  /**
   * @type string, uuid
   */
  readonly id: GenericUuid;
}
export type GenericIdReadOnlyApi = string;

export type GenericUuidReadOnlyApi = string;

export declare const enumAssetTypeV2: {
  readonly IMAGE: 'IMAGE';
  readonly LOGO: 'LOGO';
  readonly VIDEO: 'VIDEO';
};
export type EnumAssetTypeV2Api = (typeof enumAssetTypeV2)[keyof typeof enumAssetTypeV2];

export interface AssetResourceReadV2Api {
  /**
   * @type string | undefined, uuid
   */
  readonly id?: GenericUuidReadOnlyApi;
  /**
   * @type string | undefined
   */
  readonly type?: EnumAssetTypeV2Api;
}

export type ReadonlySemverApi = string;
export interface ConfigurationResourceComponentVersionApi {
  /**
   * @type string
   */
  readonly versionId: GenericIdReadOnlyApi;
  /**
   * @type string
   */
  readonly versionNumber: ReadonlySemverApi;
}

export interface ConfigurationResourceOsVersionApi {
  /**
   * @type string | undefined
   */
  readonly osId?: GenericIdReadOnlyApi;
  /**
   * @type object | undefined
   */
  readonly osLogo?: AssetResourceReadV2Api;
  /**
   * @type string | undefined
   */
  readonly osName?: string;
  /**
   * @type string | undefined
   */
  readonly osVersionId?: GenericIdReadOnlyApi;
  /**
   * @type string | undefined
   */
  readonly osVersionNumber?: ReadonlySemverApi;
}

export interface ConfigurationResourceParameterAndTemplateIdApi {
  /**
   * @type string
   */
  readonly parameterSetId: string;
  /**
   * @description Template Id ICP.
   * @type string
   */
  readonly templateId: string;
}

export interface ConfigurationResourceApplicationVersionApi {
  /**
   * @type string
   */
  readonly applicationId: GenericIdReadOnlyApi;
  /**
   * @type object
   */
  readonly applicationLogo: AssetResourceReadV2Api;
  /**
   * @type string
   */
  readonly applicationName: string;
  /**
   * @type string | undefined
   */
  readonly applicationVersionId?: GenericIdReadOnlyApi;
  /**
   * @type string | undefined
   */
  readonly applicationVersionNumber?: ReadonlySemverApi;
  /**
   * @type array | undefined
   */
  readonly applicationVersionParameterAndTemplateIds?: ConfigurationResourceParameterAndTemplateIdApi[];
  /**
   * @type string
   */
  readonly configurationModuleName?: string;
}

export type ConfigurationResourceSoftwareItemMinimalV2 = ConfigurationResourceSoftwareVersionMinimalV2 & {
  /**
   * @type integer
   */
  readonly order: number;
  /**
   * @type string
   */
  readonly type: EnumConfigurationResourceSoftwareItem;
};

export type GenericIdApi = string;

export interface DeviceGroupResourceApi {
  /**
   * @description Name of the device group.
   * @type string
   */
  readonly name: string;
}

export interface ConfigurationResourceSystemPackageVersionApi {
  /**
   * @type string
   */
  readonly configurationModuleName?: string;
  /**
   * @type string
   */
  readonly systemPackageId: GenericIdReadOnlyApi;
  /**
   * @type object
   */
  readonly systemPackageLogo: AssetResourceReadV2Api;
  /**
   * @type string
   */
  readonly systemPackageName: string;
  /**
   * @type string | undefined
   */
  readonly systemPackageVersionId?: GenericIdReadOnlyApi;
  /**
   * @type string | undefined
   */
  readonly systemPackageVersionNumber?: ReadonlySemverApi;
  /**
   * @type array | undefined
   */
  readonly systemPackageVersionParameterAndTemplateIds?: ConfigurationResourceParameterAndTemplateIdApi[];
}

export type ConfigurationResourceOsVersionMinimalBffV2Api = ConfigurationResourceOsVersionApi &
  ConfigurationResourceSoftwareVersionMinimalV2;

export type ConfigurationResourceApplicationItemMinimalBffV2Api = ConfigurationResourceApplicationVersionApi &
  ConfigurationResourceSoftwareItemMinimalV2;

export type ConfigurationResourceRemoteKeyInjectionV2 = DeviceGroupResourceApi[];

export type ConfigurationResourceSystemPackageItemMinimalBffV2Api = ConfigurationResourceSoftwareItemMinimalV2 &
  ConfigurationResourceSystemPackageVersionApi;

export interface ConfigurationResourceConfigurationDefinitionMinimalV2 {
  /**
   * @type array
   */
  readonly applicationVersions: ConfigurationResourceApplicationItemMinimalBffV2Api[];
  /**
   * @type object | undefined
   */
  readonly osVersion?: ConfigurationResourceOsVersionMinimalBffV2Api;
  /**
   * @type array | undefined
   */
  readonly remoteKeyInjection?: ConfigurationResourceRemoteKeyInjectionV2;
  /**
   * @type array
   */
  readonly systemPackageVersions: ConfigurationResourceSystemPackageItemMinimalBffV2Api[];
}

export interface ConfigurationResourceCompositionItemMinimalV2 {
  /**
   * @type string, uuid
   */
  readonly configurationId: GenericUuid;
}
export interface ConfigurationResourceDefinitionMinimalV2 {
  /**
   * @type array
   */
  readonly compositions: ConfigurationResourceCompositionItemMinimalV2[];
  /**
   * @type object | undefined
   */
  readonly configuration?: ConfigurationResourceConfigurationDefinitionMinimalV2;
}
export interface ConfigurationResourcePatchV2Api {
  /**
   * @type object | undefined
   */
  readonly definition?: ConfigurationResourceDefinitionMinimalV2;
  /**
   * @type string | undefined
   */
  readonly description?: string;
  /**
   * @type string | undefined
   */
  readonly name?: string;
}

export type ConfigurationResourceWrite = ConfigurationResourcePatchV2Api & object;

export interface CreatorMinimal {
  /**
   * @type string, uuid
   */
  readonly id: GenericUuidReadOnly;
}
export type Creator = CreatorMinimal & {
  /**
   * @type string
   */
  readonly name: EnglishFamilyAndGivenNameReadOnly;
};
export type ConfigurationResourceFront = ConfigurationResourceWrite & {
  /**
   * @type string, date-time
   */
  readonly creationDate: ReadOnlyDate;
  readonly creator: Creator;
  /**
   * @type string, uuid
   */
  readonly id: GenericUuidReadOnly;
  /**
   * @type string, date-time
   */
  readonly updateDate: ReadOnlyDate;
  readonly updatedBy: CreatorMinimalApi;
};

export enum ModuleType {
  APPLICATION = 'application-versions',
  SYSTEM_PACKAGE = 'system-package-versions',
}
export interface ModuleItemField {
  readonly logoAssetId?: string;
  readonly name: string;
  readonly configurationModuleId: string;
  readonly templateRequestId: string;
  readonly module?: string;
  readonly version: string;
  readonly sourceType: ModuleType;
}
